/**
 * main.js — Urdile Shopify Theme
 * Inicialización global: hero carousel, scroll behavior, mobile menu,
 * announcement bar y animaciones por viewport
 */

'use strict';

document.addEventListener('DOMContentLoaded', () => {

  // ============================================================
  // 1. STICKY HEADER — añade clase 'scrolled' al bajar 10px
  // ============================================================
  const stickyHeader = document.querySelector('sticky-header, .site-header-wrapper');
  if (stickyHeader) {
    const onScroll = debounce(() => {
      if (window.scrollY > 10) {
        stickyHeader.classList.add('scrolled');
      } else {
        stickyHeader.classList.remove('scrolled');
      }
    }, 50);

    window.addEventListener('scroll', onScroll, { passive: true });
    // Estado inicial
    onScroll();
  }

  // ============================================================
  // 2. HERO CAROUSEL — autoplay 5s, pause on hover, swipe touch
  // ============================================================
  initHeroCarousel();

  // ============================================================
  // 3. INTERSECTION OBSERVER — anima secciones al entrar en viewport
  // ============================================================
  initScrollAnimations();

  // ============================================================
  // 4. MOBILE MENU — toggle del menú hamburguesa
  // ============================================================
  initMobileMenu();

  // ============================================================
  // 5. ANNOUNCEMENT BAR — botón de cierre con localStorage
  // ============================================================
  initAnnouncementBar();

  // ============================================================
  // 6. TESTIMONIALS CAROUSEL
  // ============================================================
  initTestimonialsCarousel();

  // ============================================================
  // 7. PRODUCTS CAROUSEL
  // ============================================================
  initProductsCarousel();

});

// ============================================================
// Hero Carousel
// ============================================================
function initHeroCarousel() {
  const hero = document.querySelector('.section-hero, .hero');
  if (!hero) return;

  const slides = hero.querySelectorAll('.hero__slide');
  const dots = hero.querySelectorAll('.hero__dot');
  const prevBtn = hero.querySelector('.hero__arrow--prev');
  const nextBtn = hero.querySelector('.hero__arrow--next');

  if (slides.length <= 1) return;

  let currentIndex = 0;
  let autoplayInterval = null;
  let isTransitioning = false;

  const autoplay = hero.dataset.autoplay !== 'false';
  const autoplaySpeed = parseInt(hero.dataset.autoplaySpeed || '5', 10) * 1000;

  function goTo(index, direction) {
    if (isTransitioning || index === currentIndex) return;
    isTransitioning = true;

    // Ocultar slide actual
    const currentSlide = slides[currentIndex];
    currentSlide.classList.remove('hero__slide--active');
    currentSlide.setAttribute('aria-hidden', 'true');
    if (dots[currentIndex]) {
      dots[currentIndex].classList.remove('hero__dot--active');
      dots[currentIndex].setAttribute('aria-selected', 'false');
    }

    // Mostrar nuevo slide
    currentIndex = ((index % slides.length) + slides.length) % slides.length;
    const nextSlide = slides[currentIndex];
    nextSlide.classList.add('hero__slide--active');
    nextSlide.setAttribute('aria-hidden', 'false');
    if (dots[currentIndex]) {
      dots[currentIndex].classList.add('hero__dot--active');
      dots[currentIndex].setAttribute('aria-selected', 'true');
    }

    // Reset transition lock after animation
    setTimeout(() => {
      isTransitioning = false;
    }, 500);
  }

  function next() {
    goTo(currentIndex + 1);
  }

  function prev() {
    goTo(currentIndex - 1);
  }

  function startAutoplay() {
    if (!autoplay) return;
    stopAutoplay();
    autoplayInterval = setInterval(next, autoplaySpeed);
  }

  function stopAutoplay() {
    if (autoplayInterval) {
      clearInterval(autoplayInterval);
      autoplayInterval = null;
    }
  }

  // Flechas
  if (nextBtn) {
    nextBtn.addEventListener('click', () => {
      stopAutoplay();
      next();
      startAutoplay();
    });
  }

  if (prevBtn) {
    prevBtn.addEventListener('click', () => {
      stopAutoplay();
      prev();
      startAutoplay();
    });
  }

  // Dots
  dots.forEach((dot, i) => {
    dot.addEventListener('click', () => {
      stopAutoplay();
      goTo(i);
      startAutoplay();
    });
  });

  // Pause on hover
  hero.addEventListener('mouseenter', stopAutoplay);
  hero.addEventListener('mouseleave', startAutoplay);

  // Touch/swipe support
  let touchStartX = 0;
  let touchStartY = 0;

  hero.addEventListener('touchstart', (e) => {
    touchStartX = e.touches[0].clientX;
    touchStartY = e.touches[0].clientY;
  }, { passive: true });

  hero.addEventListener('touchend', (e) => {
    const dx = e.changedTouches[0].clientX - touchStartX;
    const dy = e.changedTouches[0].clientY - touchStartY;

    // Solo swipe horizontal (ignorar scroll vertical)
    if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 50) {
      stopAutoplay();
      if (dx < 0) {
        next();
      } else {
        prev();
      }
      startAutoplay();
    }
  }, { passive: true });

  // Accesibilidad: teclas de flecha cuando el hero está enfocado
  hero.setAttribute('tabindex', '0');
  hero.addEventListener('keydown', (e) => {
    if (e.key === 'ArrowLeft') {
      stopAutoplay();
      prev();
      startAutoplay();
    } else if (e.key === 'ArrowRight') {
      stopAutoplay();
      next();
      startAutoplay();
    }
  });

  // Iniciar autoplay
  startAutoplay();
}

// ============================================================
// Scroll Animations — IntersectionObserver
// ============================================================
function initScrollAnimations() {
  if (!('IntersectionObserver' in window)) return;

  const sections = document.querySelectorAll(
    '.section-stats, .section-categories, .section-featured-products, .section-brands, .section-trust, .section-testimonials, .section-newsletter'
  );

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-in');
          observer.unobserve(entry.target);
        }
      });
    },
    {
      threshold: 0.1,
      rootMargin: '0px 0px -40px 0px',
    }
  );

  sections.forEach((section) => {
    observer.observe(section);
  });

  // También observar cards individuales
  const cards = document.querySelectorAll(
    '.product-card, .category-card, .trust__item, .review-card'
  );

  const cardObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-in');
          cardObserver.unobserve(entry.target);
        }
      });
    },
    {
      threshold: 0.05,
      rootMargin: '0px 0px -20px 0px',
    }
  );

  cards.forEach((card, i) => {
    card.style.transitionDelay = `${(i % 4) * 60}ms`;
    cardObserver.observe(card);
  });
}

// ============================================================
// Mobile Menu
// ============================================================
function initMobileMenu() {
  const menuToggle = document.getElementById('MobileMenuToggle');
  const mobileMenu = document.getElementById('MobileMenu');

  if (!menuToggle || !mobileMenu) return;

  function openMenu() {
    mobileMenu.removeAttribute('hidden');
    menuToggle.setAttribute('aria-expanded', 'true');
    document.body.classList.add('site-header__menu-open');
    document.body.style.overflow = 'hidden';
    trapFocus(mobileMenu);
  }

  function closeMenu() {
    mobileMenu.setAttribute('hidden', '');
    menuToggle.setAttribute('aria-expanded', 'false');
    document.body.classList.remove('site-header__menu-open');
    document.body.style.overflow = '';
    removeTrapFocus(mobileMenu);
    menuToggle.focus();
  }

  menuToggle.addEventListener('click', () => {
    const isOpen = menuToggle.getAttribute('aria-expanded') === 'true';
    if (isOpen) {
      closeMenu();
    } else {
      openMenu();
    }
  });

  // Botones de cerrar dentro del menú
  mobileMenu.querySelectorAll('[data-close-mobile-menu]').forEach((btn) => {
    btn.addEventListener('click', closeMenu);
  });

  // Submenús desplegables en mobile
  mobileMenu.querySelectorAll('.mobile-menu__link--parent').forEach((btn) => {
    btn.addEventListener('click', () => {
      const expanded = btn.getAttribute('aria-expanded') === 'true';
      btn.setAttribute('aria-expanded', (!expanded).toString());
    });
  });

  // Cerrar con Escape
  mobileMenu.addEventListener('close', closeMenu);
}

// ============================================================
// Testimonials Carousel
// ============================================================
function initTestimonialsCarousel() {
  const carousel = document.querySelector('[data-testimonials-carousel]');
  if (!carousel) return;

  const track = carousel.querySelector('[data-testimonials-track]');
  const cards = track ? track.querySelectorAll('.review-card') : [];
  const prevBtn = carousel.querySelector('[data-testimonials-prev]');
  const nextBtn = carousel.querySelector('[data-testimonials-next]');
  const dots = carousel.querySelectorAll('[data-testimonials-dot]');

  if (cards.length <= 1) return;

  let currentIndex = 0;

  function getVisibleCount() {
    if (window.innerWidth <= 768) return 1;
    if (window.innerWidth <= 1100) return 2;
    return 3;
  }

  function goTo(index) {
    const visible = getVisibleCount();
    const maxIndex = Math.max(0, cards.length - visible);
    currentIndex = Math.max(0, Math.min(index, maxIndex));

    const cardWidth = cards[0].offsetWidth;
    const gap = parseInt(getComputedStyle(track).gap || '24', 10);
    const offset = currentIndex * (cardWidth + gap);
    track.style.transform = `translateX(-${offset}px)`;

    dots.forEach((dot, i) => {
      const isActive = i === currentIndex;
      dot.classList.toggle('is-active', isActive);
      dot.setAttribute('aria-selected', isActive.toString());
    });
  }

  if (prevBtn) prevBtn.addEventListener('click', () => goTo(currentIndex - 1));
  if (nextBtn) nextBtn.addEventListener('click', () => goTo(currentIndex + 1));

  dots.forEach((dot) => {
    dot.addEventListener('click', () => goTo(parseInt(dot.dataset.testimonialsDot, 10)));
  });

  // Touch swipe
  let touchStartX = 0;
  carousel.addEventListener('touchstart', (e) => {
    touchStartX = e.touches[0].clientX;
  }, { passive: true });
  carousel.addEventListener('touchend', (e) => {
    const dx = e.changedTouches[0].clientX - touchStartX;
    if (Math.abs(dx) > 50) goTo(currentIndex + (dx < 0 ? 1 : -1));
  }, { passive: true });

  // Recalculate on resize
  window.addEventListener('resize', debounce(() => goTo(0), 200));
}

// ============================================================
// Products Carousel
// ============================================================
function initProductsCarousel() {
  const carousel = document.querySelector('[data-products-carousel]');
  if (!carousel) return;

  const track = carousel.querySelector('[data-products-track]');
  if (!track) return;

  const prevBtn = carousel.querySelector('[data-products-prev]');
  const nextBtn = carousel.querySelector('[data-products-next]');

  function getScrollAmount() {
    const card = track.querySelector('.product-card');
    if (!card) return 240;
    const gap = parseInt(getComputedStyle(track).gap || '24', 10);
    return card.offsetWidth + gap;
  }

  function scrollBy(dir) {
    track.scrollBy({ left: dir * getScrollAmount(), behavior: 'smooth' });
  }

  if (prevBtn) prevBtn.addEventListener('click', () => scrollBy(-1));
  if (nextBtn) nextBtn.addEventListener('click', () => scrollBy(1));

  // Update arrow visibility
  function updateArrows() {
    if (!prevBtn || !nextBtn) return;
    prevBtn.style.opacity = track.scrollLeft <= 8 ? '0.3' : '1';
    prevBtn.style.pointerEvents = track.scrollLeft <= 8 ? 'none' : 'auto';
    const maxScroll = track.scrollWidth - track.clientWidth;
    nextBtn.style.opacity = track.scrollLeft >= maxScroll - 8 ? '0.3' : '1';
    nextBtn.style.pointerEvents = track.scrollLeft >= maxScroll - 8 ? 'none' : 'auto';
  }

  track.addEventListener('scroll', debounce(updateArrows, 80), { passive: true });
  updateArrows();

  // Sin swipe por JS: la pista ya desliza nativa con scroll-snap. Sumar un
  // scrollBy al soltar el dedo hacía saltar el carrusel dos tarjetas.
}

// ============================================================
// Announcement Bar
// ============================================================
function initAnnouncementBar() {
  const bar = document.querySelector('.announcement-bar');
  if (!bar) return;

  const barId = bar.dataset.id || 'announcement-bar';
  const STORAGE_KEY = `urdile_${barId}_dismissed`;

  // Si fue cerrada previamente, ocultarla
  if (localStorage.getItem(STORAGE_KEY)) {
    bar.hidden = true;
    return;
  }

  const closeBtn = bar.querySelector('.announcement-bar__close, [data-close-announcement]');
  if (closeBtn) {
    closeBtn.addEventListener('click', () => {
      bar.hidden = true;
      localStorage.setItem(STORAGE_KEY, '1');
    });
  }
}

